import { useRef, useState } from "react";
import { Barbell, Fire, Lightning, Medal, Lock, Trophy, Camera } from "@phosphor-icons/react";
import { MilestoneThumb } from "./MilestoneThumb";
import { BadgePreviewModal } from "./BadgePreviewModal";

const ICON_MAP = {
  Barbell,
  Fire,
  Lightning,
  Medal,
  Trophy,
};

const CATEGORY_LABEL = {
  workouts: "Allenamenti",
  streak: "Streak mensile",
  phase: "Fasi completate",
};

const CATEGORY_ORDER = ["workouts", "streak", "phase"];

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function BadgeCard({ badge, canEdit, onEdit, shareToken, onPreview }) {
  const Icon = ICON_MAP[badge.icon] || Medal;
  const unlocked = badge.unlocked;
  const accent =
    badge.category === "workouts" ? "#007AFF"
      : badge.category === "streak" ? "#FF9500"
        : "#D4FF00";
  const hasThumb = !!badge.thumbnail_path;
  const triggerRef = useRef(null);

  const slotInner = hasThumb ? (
    <div
      className="w-11 h-11 flex-shrink-0 relative flex items-center justify-center bg-transparent"
      aria-hidden={false}
    >
      <MilestoneThumb
        path={badge.thumbnail_path}
        shareToken={shareToken}
        alt={badge.name}
        className="max-w-full max-h-full w-full h-full object-contain object-center"
      />
      {!unlocked && (
        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#0A0A0A] border border-[#262626] flex items-center justify-center">
          <Lock size={8} className="text-zinc-500" weight="fill" />
        </div>
      )}
    </div>
  ) : (
    <div
      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 relative"
      style={{
        backgroundColor: unlocked ? `${accent}22` : "#1A1A1A",
        border: unlocked ? `1px solid ${accent}55` : "1px solid #262626",
      }}
    >
      <Icon size={20} weight="fill" style={{ color: unlocked ? accent : "#4b4b55" }} />
      {!unlocked && (
        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#0A0A0A] border border-[#262626] flex items-center justify-center">
          <Lock size={8} className="text-zinc-500" weight="fill" />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`relative rounded-xl border p-3 transition-all ${
        unlocked
          ? "bg-[#171717] border-[#262626]"
          : "bg-[#0F0F0F] border-[#1A1A1A] opacity-60"
      }`}
      data-testid={`badge-${badge.code}`}
      data-unlocked={unlocked ? "true" : "false"}
    >
      <div className="flex items-center gap-3">
        {onPreview ? (
          <button
            ref={triggerRef}
            type="button"
            onClick={() => onPreview(badge, triggerRef)}
            aria-label={`Anteprima ${badge.name}`}
            className="rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-white/30 active:scale-95 transition-transform"
            data-testid={`badge-preview-trigger-${badge.code}`}
          >
            {slotInner}
          </button>
        ) : slotInner}
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold truncate ${unlocked ? "text-white" : "text-zinc-500"}`}>
            {badge.name}
          </p>
          <p className="text-[11px] text-zinc-500 leading-snug line-clamp-2">{badge.description}</p>
          {unlocked && badge.unlocked_at && (
            <p className="text-[10px] text-zinc-600 mt-0.5">Sbloccato il {formatDate(badge.unlocked_at)}</p>
          )}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(badge); }}
            className="w-7 h-7 rounded-md bg-[#1A1A1A] hover:bg-[#262626] border border-[#262626] text-zinc-400 hover:text-white flex items-center justify-center flex-shrink-0"
            aria-label="Modifica miniatura"
            title="Carica una miniatura personalizzata"
            data-testid={`badge-edit-thumb-${badge.code}`}
          >
            <Camera size={12} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function BadgesView({ data, loading, error, compact = false, canEdit = false, onEditThumb, shareToken, canPreview }) {
  const [preview, setPreview] = useState(null);
  // By default preview is enabled in the client-facing view (shareToken set).
  const previewEnabled = canPreview ?? !!shareToken;

  const openPreview = (badge, triggerRef) => {
    setPreview({ badge, triggerRef });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="badges-loading">
        <div className="w-6 h-6 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-6 text-zinc-500 text-sm">Errore nel caricamento</div>;
  }
  if (!data?.definitions?.length) {
    return <div className="text-center py-6 text-zinc-500 text-sm">Nessun badge disponibile</div>;
  }

  const unlockedCount = data.definitions.filter((b) => b.unlocked).length;

  // Group by category
  const groups = {};
  data.definitions.forEach((b) => {
    if (!groups[b.category]) groups[b.category] = [];
    groups[b.category].push(b);
  });

  return (
    <div className="space-y-4" data-testid="badges-view">
      {/* Summary */}
      <div className="bg-gradient-to-br from-[#171717] to-[#0F0F0F] border border-[#262626] rounded-xl p-4" data-testid="badges-summary">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/30 flex items-center justify-center">
            <Trophy size={18} weight="fill" className="text-[#D4FF00]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Collezione</p>
            <p className="text-xl font-bold text-white leading-none mt-0.5">
              {unlockedCount}<span className="text-zinc-600 text-sm font-normal">/{data.definitions.length}</span>
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-2 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Allenamenti</p>
            <p className="text-base font-bold text-[#007AFF] leading-none mt-1">{data.stats?.completed_workouts ?? 0}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-2 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Streak</p>
            <p className="text-base font-bold text-[#FF9500] leading-none mt-1">{data.stats?.streak_months ?? 0}<span className="text-[10px]"> mesi</span></p>
          </div>
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-2 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Fasi</p>
            <p className="text-base font-bold text-[#D4FF00] leading-none mt-1">{data.stats?.completed_phases ?? 0}</p>
          </div>
        </div>
      </div>

      {/* Grouped badges */}
      {CATEGORY_ORDER.filter((cat) => groups[cat]?.length).map((cat) => (
        <div key={cat} data-testid={`badges-group-${cat}`}>
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600 mb-2 px-1">
            {CATEGORY_LABEL[cat]}
          </p>
          <div className={`grid gap-2 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
            {groups[cat].map((b) => (
              <BadgeCard
                key={b.code}
                badge={b}
                canEdit={canEdit}
                onEdit={onEditThumb}
                shareToken={shareToken}
                onPreview={previewEnabled ? openPreview : undefined}
              />
            ))}
          </div>
        </div>
      ))}

      {preview && (
        <BadgePreviewModal
          badge={preview.badge}
          shareToken={shareToken}
          returnFocusRef={preview.triggerRef}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
